import authService from './authService';
import config from '../config';
const CHAT_SESSIONS_API_PREFIX = '/api/v1/chat-sessions';
/**
 * Fetches a list of chat sessions for the current user.
 * @param {number} [skip=0] - The number of sessions to skip for pagination.
 * @param {number} [limit=100] - The maximum number of sessions to return.
 * @returns {Promise<Array<Object>>} - An array of session summary objects (ChatSessionResponse schema).
 * @throws {Error} If fetching fails.
 */
export const listMyChatSessions = async (skip = 0, limit = 100) => {
  const queryParams = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  const url = `${config.API_BASE_URL}${CHAT_SESSIONS_API_PREFIX}?${queryParams.toString()}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch chat sessions: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      const error = new Error(errorDetail);
      error.status = response.status;
      throw error;
    }
    const sessions = await response.json();
    return sessions;
  } catch (error) {
    console.error('[chatSessionApi] Error fetching chat sessions:', error);
    if (authService.trackNetworkError) {
        authService.trackNetworkError('listMyChatSessions', error);
    }
    throw error;
  }
};
/**
 * Fetches the detailed conversation for a specific chat session.
 * @param {string} sessionId - The ID of the session to fetch.
 * @returns {Promise<Object>} - The session details including the conversation thread (ChatSessionConversationResponse schema).
 * @throws {Error} If fetching fails. Error object will have a 'status' property.
 */
export const getChatSessionDetails = async (sessionId) => {
  if (!sessionId) {
    throw new Error("Session ID is required to fetch details.");
  }
  const url = `${config.API_BASE_URL}${CHAT_SESSIONS_API_PREFIX}/${sessionId}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch session details for ${sessionId}: ${response.status}`;
      const error = new Error(errorDetail);
      error.status = response.status;
      try {
        const errorData = await response.json();
        error.detail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw error;
    }
    const sessionDetails = await response.json();
    return sessionDetails;
  } catch (error) {
    console.error(`[chatSessionApi] Error fetching session details for ${sessionId}:`, error);
    if (authService.trackNetworkError) {
        authService.trackNetworkError('getChatSessionDetails', error);
    }
    throw error;
  }
};
/**
 * Updates the metadata of a specific session.
 * @param {string} sessionId - The ID of the session to update.
 * @param {object} updateData - The data to update (e.g., { name?: string, save?: boolean }).
 *                              The 'save' property will be mapped to 'save_enabled' for the backend.
 * @returns {Promise<Object>} - The updated session object (ChatSessionResponse schema).
 * @throws {Error} If updating fails. Error object will have a 'status' property.
 */
export const updateChatSessionMetadata = async (sessionId, updateData) => {
  if (!sessionId) {
    throw new Error("Session ID is required to update metadata.");
  }
  const url = `${config.API_BASE_URL}${CHAT_SESSIONS_API_PREFIX}/${sessionId}/metadata`;
  const requestBody = { ...updateData };
  if (requestBody.hasOwnProperty('save')) {
    requestBody.save_enabled = requestBody.save;
    delete requestBody.save;
  }
  console.log(`[chatSessionApi] Updating session metadata for ${sessionId} at: ${url} with data:`, requestBody);
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: requestBody,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update session metadata for ${sessionId}: ${response.status}`;
      const error = new Error(errorDetail);
      error.status = response.status;
      try {
        const errorData = await response.json();
        error.detail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw error;
    }
    const updatedSession = await response.json();
    console.log(`[chatSessionApi] Updated metadata for session ${sessionId}:`, updatedSession);
    return updatedSession;
  } catch (error) {
    console.error(`[chatSessionApi] Error updating session metadata for ${sessionId}:`, error);
    if (authService.trackNetworkError) {
        authService.trackNetworkError('updateChatSessionMetadata', error);
    }
    throw error;
  }
};
/**
 * Deletes a specific chat session.
 * @param {string} sessionId - The ID of the session to delete.
 * @returns {Promise<boolean>} - True if deletion was successful.
 * @throws {Error} If deletion fails. Error object will have a 'status' property.
 */
export const deleteChatSession = async (sessionId) => {
  if (!sessionId) {
    throw new Error("Session ID is required to delete a session.");
  }
  const url = `${config.API_BASE_URL}${CHAT_SESSIONS_API_PREFIX}/${sessionId}`;
  console.log(`[chatSessionApi] Deleting session: ${url}`);
  try {
    const response = await authService.authFetch(url, {
      method: 'DELETE',
    });
    if (response.status === 204) {
      console.log(`[chatSessionApi] Successfully deleted session ${sessionId}.`);
      return true;
    }
    if (response.status === 404) {
        console.warn(`[chatSessionApi] session ${sessionId} not found during delete (already gone?). Treating as success for UI.`);
        return true;
    }
    let errorDetail = `Failed to delete session ${sessionId}: ${response.status}`;
    const error = new Error(errorDetail);
    error.status = response.status;
    try {
      const errorData = await response.json();
      error.detail = errorData.detail || errorDetail;
    } catch (e) {  }
    throw error;
  } catch (error) {
    console.error(`[chatSessionApi] Error deleting session ${sessionId}:`, error);
    if (authService.trackNetworkError) {
        authService.trackNetworkError('deleteChatSession', error);
    }
    throw error;
  }
};

/**
 * Updates the context of a specific chat session.
 * @param {string} sessionId - The ID of the session to update.
 * @param {Array<Object>} context - The new array of context objects, each with 'id' and 'type'.
 * @returns {Promise<Object>} - The updated session object (ChatSessionResponse schema).
 * @throws {Error} If updating fails.
 */
export const updateChatSessionContext = async (sessionId, context) => {
  if (!sessionId) {
    throw new Error("Session ID is required to update context.");
  }
  const url = `${config.API_BASE_URL}${CHAT_SESSIONS_API_PREFIX}/${sessionId}/context`;
  const requestBody = { context };
  console.log(`[chatSessionApi] Updating session context for ${sessionId} at: ${url} with data:`, requestBody);
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: requestBody,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update session context for ${sessionId}: ${response.status}`;
      const error = new Error(errorDetail);
      error.status = response.status;
      try {
        const errorData = await response.json();
        error.detail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw error;
    }
    const updatedSession = await response.json();
    console.log(`[chatSessionApi] Updated context for session ${sessionId}:`, updatedSession);
    return updatedSession;
  } catch (error) {
    console.error(`[chatSessionApi] Error updating session context for ${sessionId}:`, error);
    if (authService.trackNetworkError) {
        authService.trackNetworkError('updateChatSessionContext', error);
    }
    throw error;
  }
};

export default {
  listMyChatSessions,
  getChatSessionDetails,
  updateChatSessionMetadata,
  deleteChatSession,
  updateChatSessionContext,
};